import { useEffect, useState, useCallback, useMemo } from "react";
import { Link, useSearchParams } from "react-router-dom";
import {
  Loader2, Search, RefreshCw, MessageSquare, ClipboardList, MailQuestion,
  PoundSterling, Image as ImageIcon, FileText, Inbox, Filter, ChevronRight,
  CheckCircle2, Send, Mail,
} from "lucide-react";
import { adminApi } from "@/lib/adminApi";
import { fmtMoney } from "@/pages/admin/util";
import { STATUS_LABEL, shortId, relTime, fmtTime } from "@/pages/admin/workspace/util";

/* ---------- Communications Hub ----------
 * Single cross-case timeline that surfaces every interaction PHS has with
 * customers and contractors. Filterable by kind, free-text search, and
 * (via querystring) case_id or contractor.
 * --------------------------------------- */

const KIND_FILTERS = [
  { k: "ALL",                 label: "Everything", icon: Inbox },
  { k: "NOTE",                label: "Internal notes", icon: MessageSquare },
  { k: "CONTRACTOR_UPDATE",   label: "Contractor", icon: ClipboardList },
  { k: "RESIDENT_UPDATE",     label: "Resident", icon: MailQuestion },
  { k: "EMAIL",               label: "Emails", icon: Mail },
  { k: "AUDIT",               label: "System / audit", icon: CheckCircle2 },
  { k: "INVOICE",             label: "Invoices", icon: PoundSterling },
  { k: "EVIDENCE",            label: "Evidence", icon: ImageIcon },
];

const CHANNEL_COLOR = {
  NOTE:              { dot: "bg-[#475569]", soft: "bg-[#475569]/10 text-[#475569]", label: "Note" },
  CONTRACTOR_UPDATE: { dot: "bg-[#B88A2A]", soft: "bg-[#B88A2A]/15 text-[#B88A2A]", label: "Contractor" },
  RESIDENT_UPDATE:   { dot: "bg-[#0F766E]", soft: "bg-[#0F766E]/15 text-[#0F766E]", label: "Resident" },
  ACTION_LOG:        { dot: "bg-[#475569]", soft: "bg-[#475569]/10 text-[#475569]", label: "Action" },
  AUDIT:             { dot: "bg-[#064E2E]", soft: "bg-[#064E2E]/10 text-[#064E2E]", label: "System" },
  INVOICE:           { dot: "bg-[#B88A2A]", soft: "bg-[#B88A2A]/15 text-[#B88A2A]", label: "Invoice" },
  EVIDENCE:          { dot: "bg-[#0F766E]", soft: "bg-[#0F766E]/15 text-[#0F766E]", label: "Evidence" },
  EMAIL:             { dot: "bg-[#475569]", soft: "bg-[#475569]/10 text-[#475569]", label: "Email" },
};

export default function CommsHub() {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [kind, setKind] = useState("ALL");
  const [search, setSearch] = useState("");
  const [searchParams, setSearchParams] = useSearchParams();
  const caseFilter = searchParams.get("case_id") || "";
  const contractorFilter = searchParams.get("contractor") || "";

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { limit: 80 };
      if (kind !== "ALL") params.kind = kind;
      if (caseFilter) params.case_id = caseFilter;
      if (contractorFilter) params.contractor = contractorFilter;
      if (search.trim()) params.q = search.trim();
      const { data } = await adminApi.get("/comms-feed", { params });
      setItems(data.items || []);
      setTotal(data.total || 0);
    } finally { setLoading(false); }
  }, [kind, caseFilter, contractorFilter, search]);

  useEffect(() => { load(); }, [load]);

  // Group items by day for a smooth timeline read.
  const groups = useMemo(() => {
    const out = new Map();
    items.forEach((it) => {
      const dt = new Date(it.at);
      const day = dt.toLocaleDateString("en-GB", { day: "numeric", month: "long", year: "numeric", weekday: "long" });
      const arr = out.get(day) || [];
      arr.push(it);
      out.set(day, arr);
    });
    return Array.from(out.entries());
  }, [items]);

  const clearScope = () => {
    searchParams.delete("case_id");
    searchParams.delete("contractor");
    setSearchParams(searchParams, { replace: true });
  };

  return (
    <div data-testid="comms-hub-page">
      <header className="flex items-end justify-between flex-wrap gap-3 mb-7">
        <div>
          <p className="text-[10.5px] tracking-[0.32em] uppercase text-[#B88A2A] font-semibold">Communications Hub</p>
          <h1 className="font-display text-[32px] lg:text-[42px] leading-[1.05] tracking-tight text-[#071A33] mt-2">One feed. Every interaction.</h1>
          <p className="text-sm text-[#475569] mt-2 max-w-2xl">
            Notes, contractor & resident updates, system events, invoices and evidence — chronological across every case.
          </p>
        </div>
        <button onClick={load} className="btn-outline h-10 py-0 px-4 text-sm inline-flex items-center gap-2" data-testid="comms-refresh">
          <RefreshCw size={14} className={loading ? "animate-spin" : ""}/> Refresh
        </button>
      </header>

      {(caseFilter || contractorFilter) && (
        <div className="bg-[#064E2E]/10 border border-[#064E2E]/30 rounded-lg px-3 py-2 text-xs text-[#064E2E] flex items-center justify-between mb-4" data-testid="comms-scope-banner">
          <span>
            Scoped to{" "}
            {caseFilter && <Link to={`/adminpanel/workspace/${caseFilter}`} className="underline">case {shortId(caseFilter)}</Link>}
            {contractorFilter && <span>contractor <strong>{contractorFilter}</strong></span>}
          </span>
          <button onClick={clearScope} className="text-[#064E2E] hover:underline" data-testid="comms-scope-clear">Clear</button>
        </div>
      )}

      {/* Channel tabs — replaces the chip strip for stronger visual hierarchy */}
      <div className="bg-white border border-[#E2DCD0] rounded-t-xl border-b-0 overflow-hidden" data-testid="comms-filters">
        <nav className="flex overflow-x-auto">
          {KIND_FILTERS.map((f) => {
            const Icon = f.icon;
            const active = kind === f.k;
            return (
              <button
                key={f.k}
                onClick={() => setKind(f.k)}
                className={`flex items-center gap-2 px-5 py-3.5 text-[13px] whitespace-nowrap border-b-2 transition ${
                  active
                    ? "border-[#064E2E] text-[#071A33] font-semibold bg-[#F7F4ED]/40"
                    : "border-transparent text-[#475569] hover:text-[#071A33] hover:bg-[#F7F4ED]/40"
                }`}
                data-testid={`comms-filter-${f.k.toLowerCase()}`}
              >
                <Icon size={13}/>{f.label}
              </button>
            );
          })}
        </nav>
        <div className="px-4 py-2.5 border-t border-[#E2DCD0] flex items-center gap-2 bg-[#F7F4ED]/40">
          <Search size={14} className="text-[#475569]"/>
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search messages, names, invoice numbers…"
            className="m-0 h-8 text-sm flex-1 bg-transparent outline-none placeholder:text-[#94A3B8]"
            data-testid="comms-search"
          />
        </div>
      </div>

      {/* Feed */}
      <div className="bg-white border border-[#E2DCD0] rounded-b-xl overflow-hidden">
        <div className="px-5 py-3 border-b border-[#E2DCD0] bg-[#F7F4ED]/60 text-[10.5px] uppercase tracking-[0.2em] text-[#475569] flex items-center justify-between font-semibold">
          <span>Timeline</span>
          <span className="tabular-nums">{items.length} of {total} events</span>
        </div>

        {loading && items.length === 0 && (
          <p className="px-4 py-10 text-center text-[#475569]"><Loader2 className="animate-spin inline" size={16}/> Loading feed…</p>
        )}

        {!loading && items.length === 0 && (
          <p className="px-4 py-10 text-center text-[#475569] italic">No matching events. Try a different filter.</p>
        )}

        {groups.map(([day, evts]) => (
          <div key={day} data-testid={`comms-day-${day}`}>
            <h3 className="px-4 py-2 text-[10px] uppercase tracking-[0.22em] text-[#B88A2A] bg-[#F7F4ED]/40 border-y border-[#E2DCD0]">{day}</h3>
            {evts.map((evt) => <FeedRow key={evt.id} evt={evt}/>)}
          </div>
        ))}
      </div>
    </div>
  );
}

function FeedRow({ evt }) {
  const ch = CHANNEL_COLOR[evt.channel] || CHANNEL_COLOR.NOTE;
  const c = evt.case;
  return (
    <article className="px-4 py-3 border-b border-[#E2DCD0] last:border-0 flex items-start gap-3" data-testid={`comms-row-${evt.id}`}>
      <span className={`shrink-0 w-2 h-2 rounded-full mt-2 ${ch.dot}`}/>
      <div className="flex-1 min-w-0">
        <header className="flex items-baseline justify-between gap-3 mb-1 flex-wrap">
          <div className="flex items-center gap-2 flex-wrap">
            <span className={`text-[10px] uppercase tracking-[0.14em] px-1.5 py-0.5 rounded ${ch.soft}`}>{ch.label}</span>
            {evt.kind && evt.channel === "AUDIT" && <span className="text-[10px] text-[#475569]">· {evt.kind.replace(/_/g, " ").toLowerCase()}</span>}
            {evt.actor && <span className="text-[10px] text-[#475569]">· {evt.actor}</span>}
          </div>
          <time className="text-[10px] text-[#475569]" title={fmtTime(evt.at)}>{relTime(evt.at)}</time>
        </header>
        <p className="text-sm text-[#071A33] whitespace-pre-wrap leading-relaxed">{evt.summary}</p>
        {c && c.id && (
          <Link to={`/adminpanel/workspace/${c.id}`} className="mt-2 inline-flex items-center gap-1.5 text-[11px] text-[#0F766E] hover:underline" data-testid={`comms-row-case-link-${evt.id}`}>
            <FileText size={11}/>
            {c.full_name || "(unknown)"}
            {c.park_name && <span className="text-[#475569]">· {c.park_name}</span>}
            {c.status && <span className="text-[#475569]">· {STATUS_LABEL[c.status] || c.status}</span>}
            <ChevronRight size={11}/>
          </Link>
        )}
      </div>
    </article>
  );
}
